import { useState } from "react";
import { parseJsonObject } from "@/types/domain";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Users, Tag, LogOut, AlertTriangle, Key, Music } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useUpdateAppConfig } from "@/hooks/useAppConfig";
import { useSpotifyImagesEnabled } from "@/hooks/useArtistImage";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import logo from "@/assets/logo-white.png";
import { useAuth } from "@/contexts/AuthContext";

interface EmergencyModeConfig {
  enabled: boolean;
  message?: string | null;
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const { session } = useAuth();

  const { data: stats } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => {
      const [usersCount, codesCount] = await Promise.all([
        supabase.from("profiles").select("*", { count: "exact", head: true }),
        supabase.from("discount_codes").select("*", { count: "exact", head: true }),
      ]);

      return {
        users: usersCount.count || 0,
        discountCodes: codesCount.count || 0,
      };
    },
  });

  // Fetch emergency mode status
  const { data: emergencyMode } = useQuery({
    queryKey: ["emergency-mode-admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_config")
        .select("value")
        .eq("key", "emergency_mode")
        .single();

      if (error) {
        return { enabled: false } as EmergencyModeConfig;
      }

      return parseJsonObject<EmergencyModeConfig>(data?.value ?? null, { enabled: false });
    },
  });

  // Toggle emergency mode mutation
  const toggleEmergencyMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase
        .from("app_config")
        .update({
          value: { enabled, message: null },
          updated_at: new Date().toISOString(),
        })
        .eq("key", "emergency_mode");

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emergency-mode-admin"] });
      toast({
        title: "Emergency mode updated",
        description: emergencyMode?.enabled
          ? "Emergency mode has been disabled"
          : "Emergency mode has been enabled",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Spotify artist-images feature toggle (global kill-switch).
  const spotifyEnabled = useSpotifyImagesEnabled();
  const updateSpotifyImages = useUpdateAppConfig<{ enabled: boolean }>("spotify_artist_images");

  const toggleSpotifyImages = (enabled: boolean) => {
    updateSpotifyImages.mutate(
      { enabled },
      {
        onSuccess: () => {
          toast({
            title: "Artist photos updated",
            description: enabled
              ? "Artist photos are now shown in the schedule"
              : "Artist photos are now hidden everywhere",
          });
        },
        onError: (error: Error) => {
          toast({
            title: "Failed to update",
            description: error.message,
            variant: "destructive",
          });
        },
      },
    );
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters",
        variant: "destructive",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same",
        variant: "destructive",
      });
      return;
    }
    setChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast({
        title: "Password updated",
        description: "Your password has been changed successfully",
      });
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: unknown) {
      toast({
        title: "Failed to update password",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Logged out",
      description: "You have been successfully logged out.",
    });
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-card/50 backdrop-blur-xl">
        <div className="container mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="PROduce Logo" className="h-10" />
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <span className="font-semibold">Admin Dashboard</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{session?.user?.email}</span>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12">
        <div className="mx-auto max-w-4xl space-y-8">
          <div>
            <h1 className="mb-2 text-4xl font-bold">Welcome, Admin</h1>
            <p className="text-muted-foreground">Manage users, subscriptions and discount codes</p>
          </div>

          {/* Stats Cards */}
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Total Users
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.users || 0}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="h-5 w-5 text-secondary" />
                  Discount Codes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.discountCodes || 0}</div>
              </CardContent>
            </Card>
          </div>

          {/* Emergency Mode Toggle */}
          <Card className={emergencyMode?.enabled ? "border-amber-500/50 bg-amber-500/5" : ""}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle
                  className={`h-5 w-5 ${emergencyMode?.enabled ? "text-amber-500" : "text-muted-foreground"}`}
                />
                Emergency Mode
              </CardTitle>
              <CardDescription>
                When enabled, a warning banner is shown to all producers with a link to Emergency
                mode in Settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="emergency-mode" className="text-base">
                    {emergencyMode?.enabled
                      ? "Emergency mode is ACTIVE"
                      : "Emergency mode is disabled"}
                  </Label>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {emergencyMode?.enabled
                      ? "Producers will see a warning banner on all pages"
                      : "Enable this during infrastructure issues (Cloudflare, API outages, etc.)"}
                  </p>
                </div>
                <Switch
                  id="emergency-mode"
                  checked={emergencyMode?.enabled || false}
                  onCheckedChange={(checked) => toggleEmergencyMutation.mutate(checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Artist Photos (Spotify) Toggle */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Music
                  className={`h-5 w-5 ${spotifyEnabled ? "text-primary" : "text-muted-foreground"}`}
                />
                Artist Photos
              </CardTitle>
              <CardDescription>
                When enabled, clicking an artist assignment in the schedule shows the artist's photo
                (fetched from Spotify). Turn this off to hide all artist photos instantly.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="artist-photos" className="text-base">
                    {spotifyEnabled ? "Artist photos are ON" : "Artist photos are OFF"}
                  </Label>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {spotifyEnabled
                      ? "Photos appear in the artist detail popup when a confident match is found"
                      : "No photos are fetched or shown anywhere in the app"}
                  </p>
                </div>
                <Switch
                  id="artist-photos"
                  checked={spotifyEnabled || false}
                  onCheckedChange={toggleSpotifyImages}
                  disabled={updateSpotifyImages.isPending}
                />
              </div>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5 text-primary" />
                Change Password
              </CardTitle>
              <CardDescription>Update your admin account password</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-w-md space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <PasswordInput
                    id="new-password"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <PasswordInput
                    id="confirm-password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
                <Button
                  onClick={handleChangePassword}
                  disabled={changingPassword || !newPassword || !confirmPassword}
                >
                  {changingPassword ? "Updating..." : "Update Password"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Action Cards */}
          <div className="grid gap-6 md:grid-cols-2">
            <Link to="/admin/users">
              <Card className="h-full cursor-pointer transition-all hover:scale-105 hover:border-primary">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-6 w-6 text-primary" />
                    User Management
                  </CardTitle>
                  <CardDescription>
                    View and manage all users, subscriptions and productions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full">
                    Open User Management
                  </Button>
                </CardContent>
              </Card>
            </Link>

            <Link to="/admin/discount-codes">
              <Card className="h-full cursor-pointer transition-all hover:scale-105 hover:border-secondary">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Tag className="h-6 w-6 text-secondary" />
                    Discount Codes
                  </CardTitle>
                  <CardDescription>
                    Create and manage discount codes for subscriptions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full">
                    Open Discount Codes
                  </Button>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
